/* A card shuffle for discovering and choosing a real, available product. */
(() => {
  const random = () => crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
  const shuffled = (items, rng = random) => {
    const result = [...new Set(items)];
    for (let i = result.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [result[i], result[j]] = [result[j], result[i]]; }
    return result;
  };
  const selectedVariant = (product, choices) => product.variants.find(variant => variant.options.every((value, i) => value === choices[i]));
  const optionAvailable = (product, choices, index, value) => {
    const name = typeof product.options[index] === 'string' ? product.options[index] : product.options[index].name;
    return product.variants.some(variant => variant.available && variant.options[index] === value && (!/size/i.test(name) || choices.every((choice, j) => j === index || !choice || variant.options[j] === choice)));
  };
  const usable = product => product?.featured_image && !product.requires_selling_plan && product.variants?.some(variant => variant.available);
  async function chooseProduct(handles, history, getProduct, rng = random) {
    const candidates = shuffled(handles.filter(handle => !history.includes(handle) && !handle.includes('custom') && handle !== 'blow-stuff-up-with-me-on-the-4th-of-july-t-shirt-1'), rng);
    for (const handle of candidates.slice(0, 4)) {
      try { const product = await getProduct(handle); if (usable(product)) return product; }
      catch (error) { if (error.name === 'AbortError') throw error; }
    }
    throw new Error('No available recommendation');
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { shuffled, selectedVariant, optionAvailable, usable, chooseProduct };
  if (typeof window === 'undefined' || customElements.get('surprise-picker')) return;

  class SurprisePicker extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      const swatches = JSON.parse(this.querySelector('[data-swatches]').textContent);
      this.swatches = new Map(swatches.pairs.split('|').reverse().map(pair => pair.split('=').map(value => value.trim())));
      this.namedColors = new Set(swatches.named);
      this.root = this.dataset.root || '/';
      this.history = [];
      this.previousDeck = [];
      this.glow = this.querySelector('[data-shuffle-glow]');
      this.busy = false;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.deck = this.querySelector('[data-deck]');
      this.shuffleAnimations = [];
      this.spinButton = this.querySelector('[data-spin]');
      this.spinLabel = this.querySelector('[data-spin-label]');
      this.skipButton = this.querySelector('[data-skip]');
      this.status = this.querySelector('[data-status]');
      this.spinControls = this.querySelector('[data-spin-controls]');
      this.spinHome = this.querySelector('[data-spin-home]');
      this.result = this.querySelector('[data-result]');
      this.productStage = this.querySelector('[data-product-stage]');
      this.showingProduct = false;
      this.stageAnimations = [];
      this.options = this.querySelector('[data-options]');
      this.form = this.querySelector('[data-product-form]');
      this.sizeSlot = this.querySelector('[data-size-slot]');
      this.addButton = this.querySelector('[data-add]');
      this.cartStatus = this.querySelector('[data-cart-status]');
      this.spinButton.disabled = true;
      this.spinButton.addEventListener('click', () => this.spin(), { signal });
      this.skipButton.addEventListener('click', () => this.finishShuffle(), { signal });
      this.querySelector('[data-product-form]').addEventListener('submit', event => { event.preventDefault(); this.add(); }, { signal });
      this.form.addEventListener('change', () => { this.cartStatus.textContent = ''; this.updateVariant(); }, { signal });
      this.motion.addEventListener('change', () => { if (this.motion.matches) { this.finishShuffle(); this.stageAnimations.forEach(animation => animation.finish()); } }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.finishShuffle(); }, { signal });
      this.deck.style.opacity = '0';
      try {
        const previous = JSON.parse(sessionStorage.getItem('illegal-surprise-lineup') || '[]');
        if (Array.isArray(previous)) this.previousDeck = previous.filter(handle => typeof handle === 'string').slice(0, 3);
      } catch (_) {}
      // Load the initial trio when the section approaches the viewport, and
      // avoid the last displayed trio even when the page has been refreshed.
      this.initialObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.initialObserver.disconnect();
        this.prepareInitialLineup();
      }, { rootMargin: '600px' });
      this.initialObserver.observe(this);

    }
    async prepareInitialLineup() {
      const owner = this.events;
      const current = () => this.isConnected && this.events === owner;
      this.spinLabel.textContent = this.labels.loading;
      try {
        const lineup = await this.loadLineup(await this.getHandles());
        if (!current()) return;
        this.mountLineup(lineup);
      } catch (_) {
        // Existing server-rendered photographs remain a usable offline fallback.
      } finally {
        if (current()) {
          this.deck.style.opacity = '1';
          if (!this.motion.matches) this.deck.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
          this.spinButton.disabled = false;
          this.spinLabel.textContent = this.labels.spin;
        }
      }
    }
    mountLineup(lineup) {
      this.previousDeck = lineup.map(item => item.product.handle);
      try { sessionStorage.setItem('illegal-surprise-lineup', JSON.stringify(this.previousDeck)); } catch (_) {}
      this.deck.replaceChildren(...lineup.map(({ product, image }) => {
        const card = document.createElement('div');
        card.className = 'surprise-card'; card.dataset.shuffleHandle = product.handle;
        image.alt = ''; card.append(image);
        return card;
      }));
    }
    async getJSON(url) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      const owner = this.events.signal;
      owner.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 5000);
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Request failed');
        const data = await response.json();
        return data.variants ? (window.ProductColorMatches?.apply(data) || data) : data;
      } finally { clearTimeout(timeout); owner.removeEventListener('abort', abort); }
    }
    async getHandles() {
      if (!this.handles) {
        const [catalog, images] = await Promise.all([this.getJSON(this.dataset.catalog), this.getJSON(this.dataset.imageMap).catch(() => ({}))]);
        this.imageMap = images;
        this.handles = catalog.shuffle.filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle));
      }
      return this.handles;
    }
    async spin() {
      if (this.busy) return;
      const owner = this.events;
      const current = () => this.isConnected && this.events === owner;
      const keyboardStart = this.spinButton.matches(':focus-visible');
      this.busy = true;
      this.dataset.state = 'loading';
      this.setOptionsDisabled(true);
      this.spinButton.disabled = this.addButton.disabled = true;
      this.spinLabel.textContent = this.labels.loading;
      this.status.classList.add('surprise-sr-only');
      this.status.textContent = this.labels.loading;
      this.cartStatus.textContent = '';
      this.querySelector('[data-cart-link]').hidden = true;
      try {
        const handles = await this.getHandles();
        const lineup = await this.loadLineup(handles);
        if (!current()) return;
        const winner = Math.floor(random() * lineup.length);
        const product = lineup[winner].product;
        this.skipRequested = this.motion.matches;
        this.shuffleAnimations.forEach(animation => animation.cancel());
        this.shuffleAnimations = [];
        this.mountLineup(lineup);
        this.showProduct(false);
        this.dataset.state = 'shuffling';
        this.spinLabel.textContent = this.labels.spinning;
        this.status.textContent = this.labels.spinning;
        this.skipButton.hidden = this.motion.matches;
        await this.animateLineup(winner);
        if (!current()) return;
        this.product = product;
        this.history = [...this.history, product.handle].slice(-5);
        this.reveal(false);
      } catch (error) {
        if (!current()) return;
        this.dataset.state = this.product ? 'result' : 'error';
        this.showProduct(Boolean(this.product));
        this.status.classList.remove('surprise-sr-only');
        this.status.textContent = this.labels.error;
      } finally {
        if (current()) {
          const restoreFocus = keyboardStart || document.activeElement === this.skipButton;
          this.busy = false;
          this.skipButton.hidden = true;
          this.spinButton.disabled = false;
          this.spinLabel.textContent = this.product ? this.labels.respin : this.labels.spin;
          this.setOptionsDisabled(false);
          if (this.product) this.updateVariant();
          if (restoreFocus) (this.product ? this.querySelector('[data-product-title]') : this.spinButton).focus({ preventScroll: true });
        }
      }
    }
    async loadLineup(handles) {
      // Every shirt shown is an actual available catalog item; the winner is
      // selected only from this fully decoded lineup, never swapped in later.
      const excluded = new Set([...this.history, ...this.previousDeck]);
      const candidates = shuffled(handles.filter(handle => !excluded.has(handle) && !handle.includes('custom') && handle !== 'blow-stuff-up-with-me-on-the-4th-of-july-t-shirt-1'));
      const lineup = [];
      for (let offset = 0; offset < 9 && lineup.length < 3; offset += 3) {
        const batch = await Promise.all(candidates.slice(offset, offset + 3).map(async handle => {
          try {
            const product = await this.getJSON(`${this.root}products/${encodeURIComponent(handle)}.js`);
            if (!usable(product)) return null;
            const image = new Image();
            image.src = this.imageURL(product.variants.find(variant => variant.available)?.featured_image || product.featured_image);
            let timer;
            try {
              await Promise.race([image.decode(), new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Image timeout')), 4000); })]);
            } finally { clearTimeout(timer); }
            return { product, image };
          } catch (error) { if (this.events.signal.aborted) throw error; return null; }
        }));
        lineup.push(...batch.filter(Boolean));
      }
      if (lineup.length < 3) throw new Error('Not enough available photos');
      return lineup.slice(0, 3);
    }
    async animatePhase(tracks, duration) {
      if (this.skipRequested || !this.isConnected) return;
      this.shuffleAnimations = tracks.map(({ element, frames, delay = 0 }) => element.animate(frames, {
        duration, delay, fill: 'both', easing: 'cubic-bezier(.45,0,.2,1)'
      }));
      await Promise.all(this.shuffleAnimations.map(animation => animation.finished.catch(() => {})));
      // Commit final poses before discarding old effects so phases join cleanly.
      this.shuffleAnimations.forEach(animation => { try { animation.commitStyles(); } catch (_) {} animation.cancel(); });
      this.shuffleAnimations = [];
    }
    async animateLineup(winnerIndex) {
      const cards = [...this.deck.children];
      if (this.skipRequested) return;
      const spread = Math.min(128, this.deck.clientWidth * .23);
      const pose = (slot, scale = .78) => {
        const x = (slot - (cards.length - 1) / 2) / Math.max(1, (cards.length - 1) / 2);
        return `translateX(-50%) translate3d(${x * spread}px,${Math.abs(x) * 15}px,0) rotate(${x * 13}deg) scale(${scale})`;
      };
      const orders = [cards.map((_,i) => i), shuffled(cards.map((_,i) => i)), shuffled(cards.map((_,i) => i)), cards.map((_,i) => i)];
      cards.forEach((card,i) => { card.style.transform = pose(i); card.style.opacity = '1'; card.style.zIndex = String(i + 1); });
      await this.animatePhase(cards.map((element,i) => ({ element, frames: orders.map((order,step) => ({
        transform: pose(order.indexOf(i), step === 1 || step === 2 ? .74 : .78),
        zIndex: String(cards.length - order.indexOf(i)), offset: [0,.34,.68,1][step]
      })) })), 1350);
      if (this.skipRequested) return;
      const winner = cards[winnerIndex];
      winner.style.zIndex = '20';
      this.glow.hidden = false;
      await this.animatePhase([
        ...cards.map((element,i) => ({ element, frames: i === winnerIndex ? [
          { transform: pose(i), opacity: 1 },
          { transform: 'translateX(-50%) translate3d(0,-12px,100px) rotate(-7deg) scale(.9)', opacity: 1 }
        ] : [
          { transform: pose(i), opacity: 1 },
          { transform: `translateX(-50%) translate3d(${i < winnerIndex ? -spread * 1.5 : spread * 1.5}px,28px,-100px) rotate(${i < winnerIndex ? -22 : 22}deg) scale(.48)`, opacity: 0 }
        ] })),
        { element: this.glow, frames: [{ opacity: 0, transform: 'scale(.65)' }, { opacity: .8, transform: 'scale(1)' }] }
      ], 420);
      if (this.skipRequested) return;
      await this.animatePhase([
        { element: winner, frames: [
          { transform: 'translateX(-50%) translate3d(0,-12px,100px) rotate(-7deg) rotateY(0deg) scale(.9)', offset: 0 },
          { transform: 'translateX(-50%) translate3d(0,-18px,60px) rotate(4deg) rotateY(360deg) scale(.94)', offset: .42 },
          { transform: 'translateX(-50%) translate3d(0,-5px,0) rotate(-2deg) rotateY(720deg) scale(1.02)', offset: .82 },
          { transform: 'translateX(-50%) translate3d(0,0,0) rotate(0deg) rotateY(720deg) scale(1)', offset: 1 }
        ] },
        { element: this.glow, frames: [{ opacity: .8, transform: 'scale(1)' }, { opacity: 1, transform: 'scale(1.18)', offset: .45 }, { opacity: 0, transform: 'scale(1.25)' }] }
      ], 1500);
      this.glow.hidden = true;
    }
    finishShuffle() { this.skipRequested = true; this.shuffleAnimations.forEach(animation => { try { animation.finish(); } catch (_) {} }); }
    setOptionsDisabled(disabled) {
      this.options.disabled = disabled;
      this.sizeSlot.querySelectorAll('select').forEach(select => { select.disabled = disabled; });
    }
    imageURL(source, width = 700) {
      if (!source) return '';
      const original = typeof source === 'string' ? source : source.src;
      const url = new URL(original, location.origin);
      const asset = this.imageMap?.[url.pathname.split('/').pop()];
      if (asset) return new URL(asset, this.dataset.assetBase.startsWith('//') ? location.protocol + this.dataset.assetBase : this.dataset.assetBase).href;
      url.searchParams.set('width', width);
      return url.href;
    }
    showProduct(show, animate = true) {
      this.showingProduct = show;
      this.stageAnimations.forEach(animation => animation.cancel());
      this.stageAnimations = [];
      this.deck.hidden = show;
      if (show) this.glow.hidden = true;
      this.productStage.hidden = !show;
      if (show && animate && !this.motion.matches) this.stageAnimations.push(this.productStage.animate([
        { opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 280, easing: 'ease-out' }));
    }
    reveal(animateImage = true) {
      this.dataset.state = 'result';
      this.dataset.hasResult = 'true';
      this.dataset.productHandle = this.product.handle;
      this.querySelector('[data-respin-slot]').append(this.spinControls);
      this.querySelector('[data-empty]').hidden = true;
      this.result.hidden = false;
      this.querySelector('[data-product-title]').textContent = this.product.title;
      this.querySelector('[data-product-link]').href = `${this.root}products/${this.product.handle}`;
      const first = this.product.variants.find(variant => variant.available);
      this.sizeSlot.replaceChildren();
      const controls = this.product.options.map((option, index) => {
        const name = typeof option === 'string' ? option : option.name;
        const group = document.createElement('fieldset'); group.className = 'surprise-option-group'; group.dataset.optionGroup = index;
        const legend = document.createElement('legend'); legend.append(document.createTextNode(`${name}: `));
        const selected = document.createElement('span'); selected.dataset.selectedOption = ''; legend.append(selected); group.append(legend);
        const row = document.createElement('div'); row.className = 'surprise-option-values';
        const values = [...new Set(this.product.variants.map(variant => variant.options[index]))];
        const isSize = /size/i.test(name);
        if (isSize) {
          const select = document.createElement('select'); select.className = 'surprise-size-select'; select.name = `surprise-option-${index}`; select.required = true; select.setAttribute('aria-label', 'Choose a size');
          const placeholder = document.createElement('option'); placeholder.value = ''; placeholder.textContent = 'Select size'; select.append(placeholder);
          values.forEach(value => { const item = document.createElement('option'); item.value = value; item.textContent = value; select.append(item); });
          if (values.length === 1) select.value = values[0];
          legend.className = 'surprise-sr-only'; group.append(select); this.sizeSlot.append(group);
          return null;
        }
        const isColor = /^colou?r$/i.test(name.trim());
        values.forEach(value => {
          const label = document.createElement('label'); label.className = 'surprise-option-choice'; label.title = value;
          const input = document.createElement('input'); input.type = 'radio'; input.name = `surprise-option-${index}`; input.value = value; input.required = true; input.setAttribute('aria-label', value); input.checked = !isSize && value === first.options[index];
          const face = document.createElement('span'); face.className = 'surprise-option-pill'; face.textContent = value;
          if (isColor) {
            const key = value.trim().toLowerCase().replace(/[\s_]+/g, '-');
            const flat = key.replace(/-/g, '');
            const color = this.swatches.get(key) || (this.namedColors.has(flat) ? flat : '');
            if (color && CSS.supports('color', color)) { face.className = 'surprise-option-swatch'; face.textContent = ''; face.style.setProperty('--swatch', color); }
          }
          face.setAttribute('aria-hidden', 'true'); label.append(input, face); row.append(label);
        });
        group.append(row);
        if (values.length === 1 && values[0] === 'Default Title') group.hidden = true;
        return group;
      });
      this.options.querySelectorAll('[data-option-group]').forEach(group => group.remove());
      this.options.append(...controls.filter(Boolean));
      this.status.textContent = `${this.labels.your_pick}: ${this.product.title}`;
      this.status.classList.add('surprise-sr-only');
      this.updateVariant();
      this.showProduct(true, animateImage);
      if (!this.motion.matches) this.result.animate([{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 360, easing: 'cubic-bezier(.22,1,.36,1)' });
    }
    updateVariant() {
      const groups = [...this.form.querySelectorAll('[data-option-group]')].sort((a,b) => Number(a.dataset.optionGroup) - Number(b.dataset.optionGroup));
      let choices = groups.map(group => group.querySelector('select, input:checked')?.value || '');
      groups.forEach((group, i) => group.querySelectorAll('input, option').forEach(input => {
        if (!input.value) return;
        input.disabled = !optionAvailable(this.product, choices, i, input.value);
        if (input.disabled) { input.checked = false; if (input.tagName === 'OPTION') input.selected = false; }
      }));
      choices = groups.map(group => group.querySelector('select, input:checked')?.value || '');
      groups.forEach((group, i) => { group.querySelector('[data-selected-option]').textContent = choices[i]; });
      this.variant = selectedVariant(this.product, choices);
      const imageVariant = this.variant || this.product.variants.find(variant => variant.available && choices.every((choice, i) => !choice || variant.options[i] === choice)) || this.product.variants.find(variant => variant.available);
      const image = this.querySelector('[data-product-image]');
      image.onerror = () => { image.onerror = null; const fallback = imageVariant?.featured_image || this.product.featured_image; image.src = typeof fallback === 'string' ? fallback : fallback.src; };
      image.src = this.imageURL(imageVariant?.featured_image || this.product.featured_image);
      image.alt = this.product.title;
      this.querySelector('[data-product-price]').textContent = this.money.format((this.variant?.price ?? imageVariant.price) / 100);
      this.addButton.disabled = this.busy || !this.variant?.available;
      this.addButton.textContent = !choices.every(Boolean) ? this.labels.choose_size : this.variant?.available ? this.labels.add : this.labels.unavailable;
      const link = this.querySelector('[data-product-link]');
      link.href = `${this.root}products/${this.product.handle}${this.variant ? `?variant=${this.variant.id}` : ''}`;
      this.querySelector('[data-image-link]').href = link.href;
    }
    async add() {
      if (this.busy || !this.variant?.available) return;
      this.busy = true;
      this.addButton.disabled = this.spinButton.disabled = true;
      this.setOptionsDisabled(true);
      this.addButton.textContent = this.labels.adding;
      this.cartStatus.textContent = '';
      try {
        // Reuse the storefront's checked cart mutation and header/drawer refresh.
        await IllegalCart.addItem(this.variant.id, 1);
        this.cartStatus.textContent = this.labels.added;
        this.querySelector('[data-cart-link]').hidden = false;
      } catch (error) { this.cartStatus.textContent = error.message || this.labels.cart_error; }
      finally {
        this.busy = false;
        this.spinButton.disabled = false;
        this.setOptionsDisabled(false);
        this.updateVariant();
      }
    }
    disconnectedCallback() {
      this.initialObserver?.disconnect();
      this.events?.abort(); this.events = null;
      this.skipRequested = true;
      this.shuffleAnimations.forEach(animation => animation.cancel());
      this.stageAnimations.forEach(animation => animation.cancel());
    }
  }
  customElements.define('surprise-picker', SurprisePicker);
})();
